import {
  type AutomationTitleMode,
  resolveAutomationName,
  resolveAutomationTitleWrite,
} from '@blocksuite/affine-block-streamlain';

type AutomationTitleIdentity = {
  title?: unknown;
  titleMode?: AutomationTitleMode | null;
  workflowName?: string | null;
};

type MutableTextLike = {
  yText?: { length: number; doc?: unknown };
  length?: number;
  replace: (index: number, length: number, text: string) => void;
  toString?: () => string;
};

type BlockModelLike = {
  props: Record<string, unknown>;
  transact?: (fn: () => void) => void;
  store?: {
    transact?: (fn: () => void) => void;
  };
};

function getTextLength(text: MutableTextLike): number {
  if (typeof text.yText?.length === 'number') {
    return text.yText.length;
  }
  if (typeof text.length === 'number') {
    return text.length;
  }
  return text.toString?.().length ?? 0;
}

/** Event name dispatched after title write to notify canvas blocks */
export const AUTOMATION_TITLE_CHANGED = 'automation-title-changed';

/**
 * Resolves the name shown by the active Automation detail sidebar.
 * Discovery is already block-scoped and reactive, so it is the preferred
 * display source. The model config remains the loading/cross-doc fallback.
 */
export function resolveAutomationDetailTitle(
  discoveredTitle: string | null | undefined,
  identity: AutomationTitleIdentity,
  defaultTitle: string
): string {
  return (
    discoveredTitle?.trim() || resolveAutomationName(identity, defaultTitle)
  );
}

/**
 * Writes automation title to BlockModel.
 * Supports both Y.Text-backed title props and legacy string-backed props.
 * Dispatches a window event so canvas block components can re-render.
 */
export function writeAutomationTitle(
  blockModel: BlockModelLike | null | undefined,
  nextTitle: string,
  source: 'user' | 'workflow' = 'user'
): boolean {
  if (!blockModel) return false;

  const next = resolveAutomationTitleWrite(
    {
      title: blockModel.props.title,
      titleMode: blockModel.props.titleMode as AutomationTitleMode | undefined,
      workflowName: blockModel.props.workflowName as string | null | undefined,
    },
    nextTitle,
    source
  );
  if (!next) return false;

  const titleProp = blockModel.props.title as unknown;

  if (
    titleProp &&
    typeof titleProp === 'object' &&
    typeof (titleProp as MutableTextLike).replace === 'function'
  ) {
    const mutableTitle = titleProp as MutableTextLike;

    // If Y.Text is detached (deleted block), skip writing.
    if (mutableTitle.yText && !mutableTitle.yText.doc) {
      return false;
    }

    blockModel.props.titleMode = next.titleMode;
    mutableTitle.replace(0, getTextLength(mutableTitle), next.title);
    window.dispatchEvent(new CustomEvent(AUTOMATION_TITLE_CHANGED));
    return true;
  }

  const writeAsString = () => {
    blockModel.props.titleMode = next.titleMode;
    blockModel.props.title = next.title;
  };
  if (typeof blockModel.transact === 'function') {
    blockModel.transact(writeAsString);
  } else if (typeof blockModel.store?.transact === 'function') {
    blockModel.store.transact(writeAsString);
  } else {
    writeAsString();
  }
  window.dispatchEvent(new CustomEvent(AUTOMATION_TITLE_CHANGED));
  return true;
}
